/**
 * Mobile Starting Pitchers Tile — DashFooter.dc.html "Probable Pitchers":
 * a gold-rule header with a More/Less expansion chevron, a vertically-scrollable
 * semi-collapsible body (150px collapsed → 360px expanded, hidden scrollbar), and
 * a 3-line entry per game (away pitcher · @ · home pitcher) with day/time at left.
 * All colors via tokens; ownership badges via the ownBadge tints.
 */
import React, { useState, useMemo } from 'react';
import { dynastyTheme, withAlpha } from '../../../services/colorService';
import MobilePlayerLink from './MobilePlayerLink';

const StartingPitchersTile = ({ starters = [], rosterPlayers = [], onPlayerClick, onGameClick }) => {
  const [open, setOpen] = useState(false);
  const C = dynastyTheme.tokens.colors;

  // Only CURRENT-roster players count as "yours". Cut players are re-injected as
  // status='former'; excluding former/inactive keeps a dropped pitcher from
  // showing a YOURS badge here.
  const rosterMlbIds = new Set(
    rosterPlayers
      .filter(p => {
        const status = p.league_context?.roster?.status;
        return status !== 'former' && status !== 'inactive';
      })
      .map(p => Number(p.ids?.mlb))
      .filter(Boolean)
  );

  // All games, chronological — DashFooter shows every game in one scroll window
  // (no day picker; the More/Less chevron grows the window instead).
  const allGames = useMemo(() => {
    const map = {};
    (Array.isArray(starters) ? starters : []).forEach(g => {
      const d = g.game_date || 'Unknown';
      if (!map[d]) map[d] = [];
      map[d].push(g);
    });
    return Object.keys(map).sort().flatMap(d => map[d]);
  }, [starters]);

  // Yours-first: matchups where EITHER listed pitcher is on the user's roster
  // (the same rosterMlbIds membership the YOURS badge uses — no new data path)
  // sort to the top; chronological order is preserved within each group
  // (Array.sort is stable). So today's 2 yours-starters land in the top 2 slots.
  const isYoursGame = (g) =>
    (g.away_pitcher?.mlb_id && rosterMlbIds.has(Number(g.away_pitcher.mlb_id))) ||
    (g.home_pitcher?.mlb_id && rosterMlbIds.has(Number(g.home_pitcher.mlb_id)));
  const displayGames = [...allGames].sort((a, b) => (isYoursGame(b) ? 1 : 0) - (isYoursGame(a) ? 1 : 0));

  const dayAbbr = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr + 'T12:00:00');
    if (isNaN(d.getTime())) return '';
    return d.toLocaleDateString('en-US', { weekday: 'short' }).toUpperCase();
  };

  // Ownership badge — tint bg + colored text (ownBadge tokens). YOURS / team / FA.
  const Badge = ({ pitcher }) => {
    if (!pitcher || !pitcher.mlb_id) return null;
    const base = { fontSize: '7px', fontWeight: '800', padding: '1px 4px', borderRadius: '2px', whiteSpace: 'nowrap', flexShrink: 0, lineHeight: 1 };
    const isYours = rosterMlbIds.has(Number(pitcher.mlb_id));
    if (isYours) return <span style={{ ...base, color: C.primary, backgroundColor: C.ownBadge.yoursBg }}>YOURS</span>;
    if (pitcher.team_name) return <span style={{ ...base, color: C.blue, backgroundColor: C.ownBadge.teamBg }}>{pitcher.team_name.substring(0, 10)}</span>;
    if (pitcher.is_free_agent !== false) return <span style={{ ...base, color: C.success, backgroundColor: C.ownBadge.faBg }}>FA</span>;
    return null;
  };

  const PName = ({ pitcher }) => {
    if (!pitcher?.name || pitcher.name === 'TBD') return <span style={{ color: C.muted, fontSize: '12px' }}>TBD</span>;
    const isYours = pitcher.mlb_id && rosterMlbIds.has(Number(pitcher.mlb_id));
    return (
      <MobilePlayerLink
        mlbId={pitcher.mlb_id}
        name={pitcher.name}
        onClick={onPlayerClick}
        style={{ fontSize: 12, fontWeight: 700, color: isYours ? C.primary : C.white, whiteSpace: 'nowrap' }}
      />
    );
  };

  // One pitcher line: name · team · badge · (W-L · ERA pushed right).
  const PitcherLine = ({ pitcher, team }) => {
    const wl = (pitcher?.wins != null && pitcher?.losses != null) ? `${pitcher.wins}-${pitcher.losses}` : null;
    const era = pitcher?.era != null ? pitcher.era.toFixed(2) : null;
    const stat = [wl, era].filter(Boolean).join(' · ');
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0, height: '15px' }}>
        <PName pitcher={pitcher} />
        {team && <span style={{ fontSize: '8.5px', color: C.muted, whiteSpace: 'nowrap' }}>{team}</span>}
        <Badge pitcher={pitcher} />
        {stat && <span style={{ marginLeft: 'auto', fontSize: '9.5px', color: C.mutedLight, whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>{stat}</span>}
      </div>
    );
  };

  // Left-rule header (gold bar + gold uppercase) + optional More/Less chevron.
  const Header = ({ withToggle }) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '9px 8px' }}>
      <span aria-hidden="true" style={{ width: 3, height: 14, background: C.primary, borderRadius: 2, flexShrink: 0 }} />
      <span style={{ fontSize: 12, fontWeight: 600, letterSpacing: '1px', textTransform: 'uppercase', color: C.primary }}>Probable Pitchers</span>
      {withToggle && (
        <span onClick={() => setOpen(o => !o)} style={{ marginLeft: 'auto', fontSize: '9.5px', fontWeight: 700, color: C.primary, cursor: 'pointer' }}>
          {open ? 'Less ⌃' : 'More ⌄'}
        </span>
      )}
    </div>
  );

  if (!allGames.length) {
    return (
      <div>
        <Header withToggle={false} />
        <p style={{ padding: '8px', color: C.muted, fontSize: '12px' }}>No games available</p>
      </div>
    );
  }

  return (
    // 12px below the tile → 12px top gap above the next label (Player Notes),
    // matching the Probable Pitchers reference (its header adds 0 top-padding).
    <div>
      <style>{`.probables-scroll::-webkit-scrollbar{height:0;width:0;}`}</style>
      <Header withToggle />
      {/* Semi-collapsible scroll window (DashFooter): collapsed shows EXACTLY 2
          matchups, expands to 360px. Rows carry NO top padding (only bottom) so the
          first row sits 6px below the label (header bottom-pad), symmetric with the
          6px above. Entry = 0 pad-top + [away 15 + @ 11 + home 15 = 41] + 8px
          pad-bottom + 1.5px separator = 50.5px → ×2 = 101px. Hidden bar. */}
      <div
        className="probables-scroll"
        style={{ maxHeight: open ? 360 : 101, overflowY: 'auto', transition: 'max-height .2s', scrollbarWidth: 'none', msOverflowStyle: 'none', padding: '0 8px' }}
      >
        {displayGames.map((g, i) => (
          <div
            key={g.game_id || i}
            onClick={() => { if (onGameClick) onGameClick(g.game_id); }}
            style={{ display: 'flex', gap: 9, padding: '0 1px 8px', borderBottom: `1.5px solid ${withAlpha(C.slate, 0.75)}`, cursor: onGameClick ? 'pointer' : 'default' }}
          >
            <div style={{ width: 30, flexShrink: 0 }}>
              <div style={{ fontSize: 9, fontWeight: 900, color: C.primary, lineHeight: '11px' }}>{dayAbbr(g.game_date)}</div>
              <div style={{ fontSize: 8, color: C.muted, fontVariantNumeric: 'tabular-nums', lineHeight: '10px' }}>{g.game_time || 'TBD'}</div>
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <PitcherLine pitcher={g.away_pitcher} team={g.away_team} />
              <div style={{ fontSize: 8, color: C.muted, textAlign: 'center', lineHeight: '11px' }}>@</div>
              <PitcherLine pitcher={g.home_pitcher} team={g.home_team} />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default StartingPitchersTile;
